/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable complexity */
import { crop, fill, limitFit, scale } from '@cloudinary/url-gen/actions/resize';
import { defaultImage } from '@cloudinary/url-gen/actions/delivery';
import { Cloudinary, type CloudinaryImage } from '@cloudinary/url-gen/index';
import { ar1X1 } from '@cloudinary/url-gen/qualifiers/aspectRatio';
import { xyCenter } from '@cloudinary/url-gen/qualifiers/gravity';
import { type ImageTyping, type MediaTyping } from '@shared/typings/general';

// Cloudinary's maximum image processing limit is 25 Megapixels.
// Images exceeding this (e.g. 89MP) cause c_crop to return a 400 error.
// We apply c_limit before cropping to downscale oversized images within safe bounds.
// 4999x4999 = ~24.99MP, just under the 25MP limit.
const MAX_PIXEL_DIMENSION = 4999;

// Fallback image for Cloudinary delivery (without extension for defaultImage parameter)
const FALLBACK_IMAGE = 'default';

// Default scale width ensures the Cloudinary placeholder plugin always generates URLs
// with a width constraint. Without this, the blur placeholder produces URLs like
// "ar_1:1,c_crop/e_blur:2000/q_auto/f_auto" (no width), which can fail for large
// source images. The responsive plugin adds its own tagged scale action on top,
// so the final rendered width is still determined by the container size.
const DEFAULT_MAX_WIDTH = 1400;

// eslint-disable-next-line max-statements
export const useMedia = (
    media: MediaTyping | null, aspectRatio?: string, assetWidth?: number, noCrop?: boolean,
): CloudinaryImage | null => {
    // Init Cloudinary Image
    const cld = new Cloudinary({ cloud: { cloudName: String(import.meta.env.VITE_CLOUDINARY_CLOUDNAME ?? '') } });

    // If no media provided, return null and let the component handle it
    if (!media?.path) return null;

    const image: CloudinaryImage | null = cld.image(media.path);

    // Set Actions
    const limitAction = limitFit(MAX_PIXEL_DIMENSION, MAX_PIXEL_DIMENSION);
    const cropAction = crop();
    const scaleAction = scale(assetWidth ?? DEFAULT_MAX_WIDTH);

    // Validate Crop
    if (!noCrop && media) {
        if (media.width) cropAction.width(media.width);
        if (media.height) cropAction.height(media.height);
        if (media.x) cropAction.x(media.x);
        if (media.y) cropAction.y(media.y);
    }

    if (aspectRatio) cropAction.aspectRatio(aspectRatio);

    // 1. limitFit: Downscale oversized images to stay within Cloudinary's 25MP limit
    // 2. crop: Apply aspect ratio and/or coordinate-based cropping
    // 3. scale: Ensure a width constraint exists for the placeholder plugin
    // 4. delivery: Set fallback image if the requested image doesn't exist
    image.resize(limitAction)
        .resize(cropAction)
        .resize(scaleAction)
        .delivery(defaultImage(FALLBACK_IMAGE));

    return image;
};

export const useProfileImage = (image: ImageTyping | null): CloudinaryImage | null => {
    const cld = new Cloudinary({ cloud: { cloudName: String(import.meta.env.VITE_CLOUDINARY_CLOUDNAME ?? '') } });

    // If no image provided, return null and let the component handle it
    if (!image?.path) return null;

    const resizeAction = fill()
        .aspectRatio(ar1X1());

    const profileImage = cld.image(image.path)
        .resize(resizeAction.gravity(xyCenter()))
        .delivery(defaultImage(FALLBACK_IMAGE));

    return profileImage;
};

// Delivers the asset at natural size: stored crop coordinates are relative to the
// uploaded original, so a scaled rendition would offset them
export const useOriginalMediaUrl = (path: string): string => {
    const cld = new Cloudinary({ cloud: { cloudName: String(import.meta.env.VITE_CLOUDINARY_CLOUDNAME ?? '') } });

    return cld.image(path)
        .delivery(defaultImage(FALLBACK_IMAGE))
        .toURL();
};
